import type { FC } from 'react';
import type { ConversationInfoResponse } from '@/entities/conversations';
import { MAIN_FIELDS_NODE_ID } from '../../lib';
import type { AttributeDefinitionsMap, NavigationNode, NodeContent } from '../../model';
import { ContentPanel } from './ContentPanel';
import { MainFieldsPanel } from './MainFieldsPanel';
import { SectionHeading } from './SectionHeading';

/**
 * Пропсы «портянки» — единого скроллящегося списка секций атрибутов.
 *
 * Иерархия из `nodes` отрисовывается целиком, в document-order. Каждая секция
 * получает якорный `id` (через `toSectionId`) для URL hash и для scrollspy.
 * Свёрнутые секции сохраняют заголовок, но скрывают своё содержимое и подсекции.
 */
type Props = {
  /** Корневые узлы иерархии — будут отрисованы в глубину рекурсивно. */
  nodes: NavigationNode[];
  /** Карта `nodeId → NodeContent` от `buildFlatContentMap`. */
  flatContentMap: Map<string, NodeContent>;
  /** Определения атрибутов для рендеринга значений. */
  attributeDefinitions: AttributeDefinitionsMap;
  /** Данные диалога — нужны для `MainFieldsPanel` (узел `MAIN_FIELDS_NODE_ID`). */
  conversation?: ConversationInfoResponse;
  /** Функция-генератор DOM `id` секции из id узла навигации. */
  toSectionId: (nodeId: string) => string;
  /** Идентификаторы свёрнутых узлов. */
  collapsedNodeIds: Set<string>;
  /** Переключение свёрнутости узла по клику на его заголовок. */
  onToggleSection: (nodeId: string) => void;
};

type NodeProps = {
  node: NavigationNode;
  depth: number;
  flatContentMap: Map<string, NodeContent>;
  attributeDefinitions: AttributeDefinitionsMap;
  conversation?: ConversationInfoResponse;
  toSectionId: (nodeId: string) => string;
  collapsedNodeIds: Set<string>;
  onToggleSection: (nodeId: string) => void;
};

/** Рендерит одну секцию (заголовок + контент + дочерние секции). */
const SectionNode: FC<NodeProps> = ({
  node,
  depth,
  flatContentMap,
  attributeDefinitions,
  conversation,
  toSectionId,
  collapsedNodeIds,
  onToggleSection,
}) => {
  const isMainFields = node.id === MAIN_FIELDS_NODE_ID;
  const content = flatContentMap.get(node.id) ?? [];
  const hasContent = Array.isArray(content) ? content.length > 0 : false;
  const shouldRenderMainFields = isMainFields && !!conversation;

  return (
    <SectionHeading
      id={toSectionId(node.id)}
      title={node.name}
      depth={depth}
      isCollapsed={collapsedNodeIds.has(node.id)}
      onToggle={() => onToggleSection(node.id)}
    >
      {shouldRenderMainFields && <MainFieldsPanel conversation={conversation!} />}
      {!shouldRenderMainFields && hasContent && (
        <ContentPanel content={content} attributeDefinitions={attributeDefinitions} />
      )}
      {node.children?.map((child) => (
        <SectionNode
          key={child.id}
          node={child}
          depth={depth + 1}
          flatContentMap={flatContentMap}
          attributeDefinitions={attributeDefinitions}
          conversation={conversation}
          toSectionId={toSectionId}
          collapsedNodeIds={collapsedNodeIds}
          onToggleSection={onToggleSection}
        />
      ))}
    </SectionHeading>
  );
};

export const ContentScroller: FC<Props> = ({
  nodes,
  flatContentMap,
  attributeDefinitions,
  conversation,
  toSectionId,
  collapsedNodeIds,
  onToggleSection,
}) => (
  <div className="flex flex-col gap-6 p-4">
    {nodes.map((node) => (
      <SectionNode
        key={node.id}
        node={node}
        depth={0}
        flatContentMap={flatContentMap}
        attributeDefinitions={attributeDefinitions}
        conversation={conversation}
        toSectionId={toSectionId}
        collapsedNodeIds={collapsedNodeIds}
        onToggleSection={onToggleSection}
      />
    ))}
  </div>
);
